import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { WrenchIcon } from "lucide-react";

import ChatbotLayout from "../../chatbot/shared/ChatbotLayout";
import Message from "../../../i18n/Message";
import Spinner from "../../../assets/icons/Spinner";
import ZeroState from "../../chatbot/shared/ZeroState";
import backend from "../../../backend/backend";

import "./ToolRunsPage.scss";

/**
 * Saved results from the free tools on the marketing site.
 *
 * This is the in-app half of the /tools funnel and the payoff for the claim flow: someone who ran
 * the Inspector before they had an account finds that result waiting here rather than an empty
 * workspace. Read-only on purpose — running a tool still happens on the public pages, which is where
 * the SEO value is and where the widgets already live. Duplicating them in the app would mean two
 * implementations of every tool and no benefit.
 */
const TOOL_LABELS = {
  "mcp-inspector": "tools_run_kind_inspector",
  "mcp-server-tester": "tools_run_kind_tester",
  "mcp-security-scanner": "tools_run_kind_scanner",
  "mcp-token-calculator": "tools_run_kind_tokens",
  "mcp-tool-schema-linter": "tools_run_kind_linter",
  "mcp-playground-preview": "tools_run_kind_preview",
};

const ToolRunsPage = () => {
  const intl = useIntl();
  const [runs, setRuns] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    backend
      .getMyToolRuns()
      .then((data) => setRuns(data || []))
      // httpAPI already handles 401 (logout) and 429 (toast); anything else lands here, and an
      // empty list would be a lie — the account may well have runs we simply could not fetch.
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPublicResult = (run) =>
    window.open("/tools/" + run.toolSlug + "/r/" + run.uid, "_blank", "noopener");

  const formatDate = (value) =>
    value ? intl.formatDate(value, { dateStyle: "medium", timeStyle: "short" }) : "";

  return (
    <ChatbotLayout contentPanelClassName="tool_runs_page">
      <div className="breadcrumbs">&nbsp;</div>
      <h1 className="tool_runs_title">
        <Message id="tools_runs_title" />
      </h1>
      <p className="tool_runs_subtitle">
        <Message id="tools_runs_subtitle" />
      </p>

      {runs === null && !failed && <Spinner />}

      {failed && (
        <div className="tool_runs_error">
          <Message id="tools_runs_load_failed" />{" "}
          <button className="btn btn-link" onClick={load}>
            <Message id="retry" />
          </button>
        </div>
      )}

      {runs !== null && runs.length === 0 && (
        <ZeroState
          icon={<WrenchIcon color="#67748e" size="36" />}
          textKey="tools_runs_zero_state"
          buttonKey="tools_runs_zero_state_button"
          onClick={() => window.open("/tools", "_blank", "noopener")}
        />
      )}

      {runs !== null && runs.length > 0 && (
        <div className="tool_runs_list">
          {runs.map((run) => (
            <div className="tool_run_row" key={run.uid}>
              <div className="tool_run_main">
                <div className="tool_run_kind">
                  <Message id={TOOL_LABELS[run.toolSlug] || "tools_run_kind_other"} />
                  {run.grade && <span className="tool_run_grade">{run.grade}</span>}
                </div>
                {/* Already credential-masked server-side before it was ever stored. */}
                <div className="tool_run_url" title={run.targetUrl}>
                  {run.targetUrl}
                </div>
                <div className="tool_run_meta">
                  {run.serverName && <span>{run.serverName}</span>}
                  {typeof run.toolCount === "number" && (
                    <span>
                      {run.toolCount} <Message id="tools_runs_tools" />
                    </span>
                  )}
                  <span>{formatDate(run.dateCreated)}</span>
                </div>
              </div>
              <div className="tool_run_actions">
                <button
                  className="btn btn-outline-dark"
                  onClick={() => openPublicResult(run)}
                >
                  <Message id="tools_runs_open" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ChatbotLayout>
  );
};

export default ToolRunsPage;
